import React, { useCallback, useEffect, useState } from "react";

import {
  Table,
  Button,
  Input,
  Space,
  Typography,
  Modal,
  Form,
  Upload,
  Switch,
  Tag,
  Tooltip,
} from "antd";
import { Search, Plus, Edit, Trash2, Delete } from "lucide-react";
import apiPath from "../../constants/apiPath";
import useRequest from "../../hooks/useRequest";
import apiClient from "../../helper/apiClient";
import { ShowToast, Severty } from "../../helper/toast";
import dayjs from "dayjs";

const { Title } = Typography;

const FileType = ["image/png", "image/jpg", "image/jpeg", "image/webp"];

const getSingleCategoryUrl = (id) => `${apiPath.categories}${id}/`;

const CategoryIndex = () => {
  const { request } = useRequest();
  const [form] = Form.useForm();
  const [preview, setPreview] = useState("");
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [editingRecord, setEditingRecord] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadedImageUrl, setUploadedImageUrl] = useState("");

  const normalizeCategory = (item, index) => ({
    key: item?.id ?? item?._id ?? `${index}`,
    id: item?.id ?? item?._id ?? index + 1,
    srNo: index + 1,
    title: item?.title ?? item?.name ?? "",
    description: item?.description ?? "",
    categoryImage:
      item?.image_url || item?.image || item?.imageUrl || item?.thumbUrl || "",
    status: item?.status ?? true, // Added status field
    createdAt: item?.created_at || item?.createdAt || "",
  });

  const handleUpload = async (file) => {
    try {
      setUploadLoading(true);

      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(file);

      const formData = new FormData();
      formData.append("image", file);

      const res = await apiClient.post(
        `/api/organizer/image_upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      const imageUrl = res.data?.data?.image_url;

      if (!imageUrl) {
        throw new Error(res.data?.message || "Upload failed");
      }

      setUploadedImageUrl(imageUrl);

      form.setFieldsValue({
        categoryImage: imageUrl,
      });

      ShowToast("Image uploaded successfully", Severty.SUCCESS);
    } catch (err) {
      console.error("UPLOAD ERROR:", err);
      ShowToast("Image upload failed", Severty.ERROR);
      setUploadedImageUrl("");
    } finally {
      setUploadLoading(false);
    }

    return false;
  };

  const resolveList = (data) => {
    const list = data?.data || data?.results || data?.categories || data || [];
    return Array.isArray(list) ? list : [];
  };

  const syncFormFromCategory = useCallback(
    (category) => {
      const image = category?.categoryImage || "";
      form.setFieldsValue({
        title: category?.title || "",
        description: category?.description || "",
        categoryImage: image,
        status: category?.status !== undefined ? category.status : true, // Added status sync
      });
      setPreview(image);
      setUploadedImageUrl(image);
    },
    [form],
  );

  const fetchCategories = useCallback(() => {
    setLoading(true);

    request({
      url: apiPath.categories,
      method: "GET",
      onSuccess: (data) => {
        const normalizedData = resolveList(data).map(normalizeCategory);
        setCategories(normalizedData);
        setLoading(false);
      },
      onError: (error) => {
        setLoading(false);
        setCategories([]);
        ShowToast(
          error?.response?.data?.message || "Failed to load categories",
          Severty.ERROR,
        );
      },
    });
  }, [request]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const resetModalState = () => {
    setEditingRecord(null);
    setPreview("");
    setUploadedImageUrl("");
    setDetailLoading(false);
    setModalLoading(false);
    form.resetFields();
  };

  const openCreateModal = () => {
    resetModalState();
    // Set default status to true for new categories
    form.setFieldsValue({ status: true });
    setModalOpen(true);
  };

  const openEditModal = (record) => {
    resetModalState();
    setEditingRecord(record);
    setModalOpen(true);
    setDetailLoading(true);

    request({
      url: getSingleCategoryUrl(record.id),
      method: "GET",
      onSuccess: (data) => {
        const category = normalizeCategory(
          data?.data || data?.result || data,
          0,
        );
        // console.log(data?.data);
        // console.log(category.id);

        syncFormFromCategory(category);
        setEditingRecord(category);
        setDetailLoading(false);
      },
      onError: (error) => {
        setDetailLoading(false);
        ShowToast(
          error?.response?.data?.message || "Failed to load category details",
          Severty.ERROR,
        );
      },
    });
  };

  const handleStatusToggle = async (checked, record) => {
    try {
      const formData = new FormData();
      formData.append("status", checked);

      await request({
        url: getSingleCategoryUrl(record.id),
        method: "PATCH",
        data: formData,
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onSuccess: (data) => {
          ShowToast(
            `Status updated to ${checked ? "Active" : "Inactive"}`,
            Severty.SUCCESS,
          );
          fetchCategories(); // Refresh the list
        },
        onError: (error) => {
          ShowToast(
            error?.response?.data?.message || "Failed to update status",
            Severty.ERROR,
          );
        },
      });
    } catch (error) {
      ShowToast("Failed to update status", Severty.ERROR);
    }
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: "Delete Category",
      content: `Are you sure you want to delete "${record.title}"?`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () =>
        new Promise((resolve, reject) => {
          request({
            url: getSingleCategoryUrl(record.id),
            method: "DELETE",
            onSuccess: (data) => {
              ShowToast(
                data?.message || "Category deleted successfully",
                Severty.SUCCESS,
              );
              fetchCategories();
              resolve();
            },
            onError: (error) => {
              ShowToast(
                error?.response?.data?.message || "Failed to delete category",
                Severty.ERROR,
              );
              reject(error);
            },
          });
        }),
    });
  };

  const handleSubmit = async (values) => {
    const title = values.title?.trim();
    const description = values.description?.trim();
    const status = values.status !== undefined ? values.status : true; // Get status value

    let imageUrl = uploadedImageUrl;

    if (!imageUrl && editingRecord && editingRecord.categoryImage) {
      imageUrl = editingRecord.categoryImage;
    }

    if (!editingRecord && !imageUrl) {
      ShowToast("Please upload an image", Severty.ERROR);
      return;
    }

    const formData = new FormData();

    if (title) formData.append("title", title);
    if (description) formData.append("description", description);
    if (imageUrl) formData.append("image_url", imageUrl);
    formData.append("status", status); // Add status to form data

    setModalLoading(true);

    request({
      url: editingRecord
        ? getSingleCategoryUrl(editingRecord.id)
        : apiPath.categories,
      method: editingRecord ? "PATCH" : "POST",
      data: formData,
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onSuccess: (data) => {
        setModalLoading(false);
        ShowToast("Category saved successfully", Severty.SUCCESS);
        setModalOpen(false);
        resetModalState();
        fetchCategories();
      },
      onError: (error) => {
        setModalLoading(false);
        ShowToast(
          error?.response?.data?.message || "Failed to save category",
          Severty.ERROR,
        );
      },
    });
  };

  const filteredCategories = categories.filter((item) => {
    const query = searchText.trim().toLowerCase();
    if (!query) return true;
    return (
      item.title.toLowerCase().includes(query) ||
      item.description.toLowerCase().includes(query)
    );
  });
  const handleDeleteImage = async (id) => {
    try {
      const res = await apiClient.delete(apiPath.categoryPhotoDelete(id));

      ShowToast(res.data.message, Severty.SUCCESS);

      setPreview("");
      setUploadedImageUrl("");

      form.setFieldsValue({
        categoryImage: "",
      });

      if (editingRecord) {
        setEditingRecord({
          ...editingRecord,
          categoryImage: "",
        });
      }
    } catch (err) {
      ShowToast(
        err?.response?.data?.message || "Failed to delete image",
        Severty.ERROR,
      );
    }
  };
  const NO_IMAGE_FALLBACK =
    "data:image/svg+xml;utf8," +
    encodeURIComponent(
      `<svg xmlns='http://www.w3.org/2000/svg' width='50' height='50'>
       <rect width='100%' height='100%' fill='#f0f0f0'/>
       <text x='50%' y='50%' font-size='9' fill='#999' text-anchor='middle' dy='.3em'>No Image</text>
     </svg>`,
    );

  const columns = [
    {
      title: "SR No.",
      dataIndex: "srNo",
      key: "srNo",
      width: 80,
      render: (srNo) => srNo || "-",
    },
    {
      title: "Category Image",
      dataIndex: "categoryImage",
      key: "categoryImage",
      width: 100,
      render: (image) => (
        <img
          src={image || NO_IMAGE_FALLBACK}
          onError={(e) => {
            if (e.currentTarget.src !== NO_IMAGE_FALLBACK) {
              e.currentTarget.src = NO_IMAGE_FALLBACK;
            }
          }}
          alt="Category"
          style={{ width: 50, height: 50, objectFit: "cover", borderRadius: 6 }}
        />
      ),
    },

    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (title) => title || "-",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (description) => description || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status, record) => (
        <Switch
          checked={status === true}
          onChange={(checked) => handleStatusToggle(checked, record)}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
        />
      ),
    },
    {
      title: "Created Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 120,
      render: (date) => {
        if (!date) return "-";
        return dayjs(date).format("YYYY-MM-DD");
      },
    },
    {
      title: "Action",
      key: "action",
      align: "right",
      width: 100,
      render: (_, record) => (
        <div
          style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}
          onClick={(e) => e.stopPropagation()}
        >
          <Tooltip title="Edit Category">
            <Button
              type="text"
              icon={<Edit size={16} style={{ color: "#10b981" }} />}
              onClick={() => openEditModal(record)}
            />
          </Tooltip>
          <Tooltip title="Delete Category">
            <Button
              type="text"
              danger
              icon={<Trash2 size={16} style={{ color: "#ef4444" }} />}
              onClick={() => handleDelete(record)}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <>
      <div
        className="products-container"
        style={{ padding: "24px", background: "#fff", borderRadius: "10px" }}
      >
        <div className="products-header">
          <Title level={3} className="products-title">
            Category
          </Title>
          <Space className="products-actions">
            <Button icon={<Plus size={16} />} onClick={openCreateModal}>
              Add Category
            </Button>
          </Space>
        </div>

        <div className="products-search-section">
          <Space size="large">
            <Input
              placeholder="Search"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              suffix={<Search size={16} color="#94a3b8" />}
              className="products-search-input"
            />
          </Space>
        </div>

        <Table
          scroll={{ x: "max-content" }}
          columns={columns}
          dataSource={filteredCategories}
          loading={loading}
          pagination={false}
          rowKey="key"
          className="products-table reward-table-custom"
        />
      </div>

      <Modal
        title={editingRecord ? "Edit Category" : "Add Category"}
        open={modalOpen}
        onCancel={() => {
          if (!modalLoading && !detailLoading) {
            setModalOpen(false);
            resetModalState();
          }
        }}
        onOk={() => form.submit()}
        okText={editingRecord ? "Update" : "Create"}
        confirmLoading={modalLoading}
        destroyOnClose
      >
        {detailLoading ? (
          <div style={{ padding: "24px 0", textAlign: "center" }}>
            Loading...
          </div>
        ) : (
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Form.Item
              label="Category Title"
              name="title"
              rules={[
                { required: true, message: "Please enter category title" },
              ]}
            >
              <Input placeholder="Enter category title" />
            </Form.Item>

            <Form.Item label="Description" name="description">
              <Input.TextArea
                rows={3}
                placeholder="Enter category description"
              />
            </Form.Item>

            {/* <Form.Item
              label="Status"
              name="status"
              valuePropName="checked"
              initialValue={true}
            >
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item> */}

            <Form.Item
              label="Upload Image"
              name="categoryImage"
              rules={[
                {
                  required: !editingRecord,
                  message: "Image is required for new categories",
                },
              ]}
              getValueFromEvent={(e) => e}
            >
              <Upload
                showUploadList={false}
                beforeUpload={handleUpload}
                disabled={uploadLoading}
                listType="picture"
              >
                <div
                  style={{
                    border: "2px dashed #d9d9d9",
                    borderRadius: "8px",
                    padding: "20px",
                    textAlign: "center",
                    cursor: "pointer",
                    minHeight: "120px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: uploadLoading ? "#f5f5f5" : "white",
                  }}
                >
                  {uploadLoading ? (
                    <p>Uploading...</p>
                  ) : preview ? (
                    <img
                      src={preview}
                      alt="preview"
                      style={{ maxHeight: 100, borderRadius: 6 }}
                    />
                  ) : (
                    <div>
                      <p>Click to upload image</p>
                      <p style={{ fontSize: 12, color: "#999", marginTop: 8 }}>
                        Supported: PNG, JPG, JPEG, WEBP
                      </p>
                    </div>
                  )}
                </div>
              </Upload>
              {/* <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                class="bi bi-archive-fill"
                viewBox="0 0 16 16"
              >
                <path d="M12.643 15C13.979 15 15 13.845 15 12.5V5H1v7.5C1 13.845 2.021 15 3.357 15zM5.5 7h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1 0-1M.8 1a.8.8 0 0 0-.8.8V3a.8.8 0 0 0 .8.8h14.4A.8.8 0 0 0 16 3V1.8a.8.8 0 0 0-.8-.8z" />
              </svg> */}

              <i
                className="bi bi-trash"
                onClick={() =>
                  editingRecord && handleDeleteImage(editingRecord.id)
                }
              ></i>
            </Form.Item>
          </Form>
        )}
      </Modal>
    </>
  );
};

export default CategoryIndex;
